import { MAP_HEIGHT, MAP_WIDTH, RATE_PILLS, STATE_PATHS } from "./usMap";
/* Marketplace-style rate map, rendered as a dotted halftone US in a magenta→purple
   gradient on a dark stage, with a glowing pin + price pill over each major metro.

   Geometry is precomputed by scripts/generate-us-map.mjs (us-atlas TopoJSON projected
   through d3-geo's geoAlbersUsa), so nothing here is a hand-drawn path and the map
   library never reaches the client bundle. Rates are average daily rental rates per
   metro, not live inventory.

   The halftone is a dot pattern used as a luminance mask over a gradient rect, all
   clipped to the landmass — so the gradient colors the dots and the dots only appear
   inside the country. Rendered as inline SVG: no API key, no network request, crisp at
   any card size. */
/* Minimum empty gap (viewBox px) required between any two rendered pills. */
const PILL_GAP = 5;
/* Resolve label collisions up front: walk the metros in order and keep a pill only if its
   box stays at least PILL_GAP away from every pill already kept — otherwise drop the whole
   marker (pin + pill). This guarantees no two pills touch, regardless of the source data. */
function getVisiblePills() {
    const kept = [];
    return RATE_PILLS.flatMap((pill) => {
        const w = `$${pill.rate}/day`.length * 12 + 26;
        const h = 40;
        const left = pill.x - w / 2;
        const top = pill.y - h - 14; // pill sits above its pin
        const collides = kept.some((k) => left < k.left + k.w + PILL_GAP &&
            left + w + PILL_GAP > k.left &&
            top < k.top + k.h + PILL_GAP &&
            top + h + PILL_GAP > k.top);
        if (collides)
            return [];
        kept.push({ x: pill.x, y: pill.y, w, h, left, top });
        return [{ ...pill, w, h }];
    });
}
const visiblePills = getVisiblePills();
export function UsRateMap() {
    return (<div aria-hidden="true" className="absolute inset-0 overflow-hidden bg-[#0E0A1C]">
      {/* Soft ambient glow behind the map so the dark stage isn't flat. */}
      <div className="pointer-events-none absolute inset-0" style={{
            background: "radial-gradient(80% 70% at 50% 45%, rgba(138,45,201,0.28), transparent 70%)",
        }}/>
      <svg viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} 
    /* `meet`, not `slice`: keep the whole country and every pill in frame; the dark
       stage fills any letterbox space so nothing is cropped. */
    preserveAspectRatio="xMidYMid meet" className="relative h-full w-full">
        <defs>
          <radialGradient id="rateDotGrad" cx="50%" cy="45%" r="72%">
            <stop offset="0%" stopColor="#5A2AA8"/>
            <stop offset="55%" stopColor="#9B2FD0"/>
            <stop offset="100%" stopColor="#EC46C8"/>
          </radialGradient>

          <radialGradient id="ratePinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF6FE8" stopOpacity={0.9}/>
            <stop offset="100%" stopColor="#FF6FE8" stopOpacity={0}/>
          </radialGradient>

          {/* Halftone grid: one small dot per tile. */}
          <pattern id="rateDots" width={13} height={13} patternUnits="userSpaceOnUse">
            <circle cx={6.5} cy={6.5} r={2.1} fill="#fff"/>
          </pattern>

          {/* White dots reveal the gradient beneath; black hides the gaps. */}
          <mask id="rateDotMask" maskUnits="userSpaceOnUse" x={0} y={0} width={MAP_WIDTH} height={MAP_HEIGHT}>
            <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill="#000"/>
            <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill="url(#rateDots)"/>
          </mask>

          {/* Union of the state shapes limits the dots to the landmass. */}
          <clipPath id="rateUsClip">
            {STATE_PATHS.map((d, i) => (<path key={i} d={d}/>))}
          </clipPath>
        </defs>

        <g clipPath="url(#rateUsClip)">
          <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill="url(#rateDotGrad)" mask="url(#rateDotMask)"/>
        </g>

        {visiblePills.map(({ city, rate, x, y, w, h }, i) => {
            const label = `$${rate}/day`;
            /* Staggered, non-uniform delay so pins breathe independently rather than
               in sync — a subtle ripple across the map. */
            const delay = `${(i * 0.37) % 3.4}s`;
            return (<g key={city}>
              {/* Glowing pin — only the dot breathes; the price pill below stays still. */}
              <g className="rate-dot" style={{ animationDelay: delay }}>
                <circle cx={x} cy={y} r={17} fill="url(#ratePinGlow)"/>
                <circle cx={x} cy={y} r={6.5} fill="#fff"/>
                <circle cx={x} cy={y} r={6.5} fill="none" stroke="#FFB3F0" strokeOpacity={0.9} strokeWidth={2}/>
              </g>

              <g transform={`translate(${x - w / 2}, ${y - h - 14})`}>
                <rect x={0} y={0} width={w} height={h} rx={h / 2} fill="#FFFFFF"/>
                <text x={w / 2} y={h / 2} textAnchor="middle" dominantBaseline="central" fontSize={19} fontWeight={700} fill="#0B0B0B" 
            /* font-sans so the pills use the same family as the rest of the page. */
            className="font-sans">
                  {label}
                </text>
              </g>
            </g>);
        })}
      </svg>
    </div>);
}
